import { Injectable, computed, inject } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';
import { Observable, catchError, forkJoin, from, of, take, tap, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import type { Address } from 'viem';

import { API_SERVICE, type CustosApi } from '../../core/api/api.provider';
import { MarketCacheService } from '../../core/services/market-cache.service';
import { WalletService } from '../../core/services/wallet';
import { CUSTOS_ENVIRONMENT, type CustosEnvironment } from '../../core/models/environment';
import { ERC20_ABI } from '../../core/contracts/abis/erc20.abi';
import type { ApiLocker, LockerRecord } from '../../core/api/api.types';
import { PREDICT_MARKET_ABI } from '../../core/contracts/abis/predict-market.abi';
import { safeBigInt, weiToHuman, parseMarketId } from '../../core/data/data-transform';
import type { PortfolioReward } from '../../shared/models';

interface PortfolioStoreState {
  rewards: PortfolioReward[];
  loadingCount: number;
  error: string | null;
  rewardsFetchedAt: number | null;
  hasMoreRewards: boolean;
}

const INITIAL_STATE: PortfolioStoreState = {
  rewards: [],
  loadingCount: 0,
  error: null,
  rewardsFetchedAt: null,
  hasMoreRewards: false,
};

// Cache duration: 5 minutes
const CACHE_DURATION_MS = 5 * 60 * 1000;

// Map raw viem/wallet revert shapes to user-friendly settle-dialog errors.
// Ordering matters — most specific first (CycleNotSettled is a subset of
// "execution reverted"). Keep this a pure function so it can be unit-tested
// independently of the RPC stack.
function mapSettleError(err: Error): Error {
  const msg = err.message || '';
  if (msg.includes('CycleNotSettled') || msg.includes('gas limit too high') || msg.includes('Internal error')) {
    return new Error('Cycle not yet settled. Please wait for the cycle to be resolved before settling your orders.');
  }
  if (msg.includes('User rejected') || msg.includes('User denied')) {
    return new Error('Transaction cancelled.');
  }
  if (msg.includes('insufficient funds') || msg.includes('insufficient balance')) {
    return new Error('Insufficient balance to cover gas for settlement.');
  }
  if (msg.includes('execution reverted')) {
    return new Error('Settlement reverted on-chain. Please try again in a moment.');
  }
  return err;
}

@Injectable({ providedIn: 'root' })
export class PortfolioStore {
  private readonly api = inject<CustosApi>(API_SERVICE);
  private readonly marketCache = inject(MarketCacheService);
  private readonly wallet = inject(WalletService);
  private readonly env = inject<CustosEnvironment>(CUSTOS_ENVIRONMENT);
  private readonly state = signalState<PortfolioStoreState>(INITIAL_STATE);

  // ==========================================================
  // Convenience getters
  // ==========================================================

  private get chainId(): number {
    return this.env.chainId;
  }

  private get walletAddress(): Address | null {
    return this.wallet.address();
  }

  // ==========================================================
  // Contract helpers (same pattern as PredictionStore)
  // ==========================================================

  private readContract<T>(args: {
    address: Address;
    abi: readonly unknown[];
    functionName: string;
    args?: readonly unknown[];
  }): Observable<T> {
    const client = this.wallet.getPublicClient();
    return from(
      client.readContract(
        args as Parameters<typeof client.readContract>[0],
      ) as Promise<T>,
    );
  }

  private writeContract(args: {
    address: Address;
    abi: readonly unknown[];
    functionName: string;
    args?: readonly unknown[];
    account: Address;
  }): Observable<`0x${string}`> {
    const wc = this.wallet.getWalletClient();
    return from(
      wc.writeContract(args as Parameters<typeof wc.writeContract>[0]),
    );
  }

  // ==========================================================
  // State Selectors
  // ==========================================================

  readonly isLoading = computed(() => this.state().loadingCount > 0);
  readonly error = computed(() => this.state().error);
  readonly rewards = computed(() => this.state().rewards);
  readonly hasMoreRewards = computed(() => this.state().hasMoreRewards);

  // Claim button visibility — reward side only. Earnings-side claim
  // (closedPredictions) moved to PortfolioDisplayStore.
  readonly hasClaimableRewards = computed(() => this.rewards().some(r => r.amount > 0));

  // ==========================================================
  // Private helpers
  // ==========================================================

  private startLoading(): void {
    patchState(this.state, { loadingCount: this.state().loadingCount + 1 });
  }

  private stopLoading(): void {
    patchState(this.state, { loadingCount: Math.max(0, this.state().loadingCount - 1) });
  }

  private isCacheValid(fetchedAt: number | null): boolean {
    return fetchedAt !== null && Date.now() - fetchedAt < CACHE_DURATION_MS;
  }


  // ==========================================================
  // Load Methods
  // ==========================================================
  loadRewards(forceRefresh = false): void {
    if (!forceRefresh && this.isCacheValid(this.state().rewardsFetchedAt)) return;

    this.startLoading();

    const addr = this.walletAddress;
    if (!addr) {
      this.stopLoading();
      patchState(this.state, { rewards: [], rewardsFetchedAt: Date.now(), hasMoreRewards: false });
      return;
    }

    // #137: Reward History = rows from locker_record, filtered to this user.
    // Need the locker list to resolve decimals + token symbol per locker_id.
    forkJoin({
      lockers: this.api.getLockers({ chain_id: this.chainId }).pipe(
        catchError(() => of([] as ApiLocker[])),
      ),
      records: this.api.getLockerRecords({ limit: 500, user_address: addr }).pipe(
        catchError(() => of([] as LockerRecord[])),
      ),
    }).pipe(
      map(({ lockers, records }) => {
        const decimalsMap = new Map(lockers.map(l => [l.locker_id, l.base_token_decimals ?? 18]));
        const symbolMap = new Map(lockers.map(l => [l.locker_id, l.locker_id]));
        return records.map((r) => ({
          token: symbolMap.get(r.locker_id) ?? r.locker_id,
          amount: weiToHuman(safeBigInt(r.reward_amount), decimalsMap.get(r.locker_id) ?? 18),
          time: new Date(r.date).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
          }),
        } satisfies PortfolioReward));
      }),
      catchError(() => of([] as PortfolioReward[])),
      take(1),
      tap((items) => {
        this.stopLoading();
        patchState(this.state, { rewards: items, rewardsFetchedAt: Date.now(), hasMoreRewards: false });
      }),
      catchError((err: Error) => {
        console.error('[PortfolioStore] Failed to load rewards:', err);
        this.stopLoading();
        patchState(this.state, {
          error: err.message || 'Failed to load rewards',
        });
        return of(null);
      }),
    ).subscribe();
  }

  loadMoreRewards(): void {
    // Staking rewards are fetched all at once; no pagination needed.
  }

  /**
   * Returns an Observable of unsettled markets -- caller subscribes
   * (needed for dialog-opening patterns in page components).
   *
   * Excludes orders from the current (latest) active cycle — only counts orders
   * from previous cycles that are actually settleable (issue #135).
   */
  getUnsettledMarkets$(): Observable<{ marketId: string; contractAddress: string; count: number; baseTokenSymbol: string }[]> {
    const addr = this.walletAddress;
    if (!addr) return of([]);

    return this.marketCache.getMarkets().pipe(
      switchMap((markets) => {
        if (markets.length === 0) return of([]);
        const requests = markets.map((m) => {
          const { base } = parseMarketId(m.market_id);
          return this.getSettleableUnsettledCount$(m.prediction_contract as Address).pipe(
            map((count) => ({
              marketId: m.market_id,
              contractAddress: m.prediction_contract as string,
              count,
              baseTokenSymbol: base,
            })),
            catchError(() =>
              of({ marketId: m.market_id, contractAddress: m.prediction_contract as string, count: 0, baseTokenSymbol: base }),
            ),
          );
        });
        return forkJoin(requests);
      }),
      map((results) => results.filter((r) => r.count > 0)),
      take(1),
      catchError((err: Error) => {
        console.error('[PortfolioStore] Failed to get unsettled markets:', err);
        return of([] as { marketId: string; contractAddress: string; count: number; baseTokenSymbol: string }[]);
      }),
    );
  }

  /**
   * Read unsettled order IDs for the wallet on a given market and return the
   * count of orders that belong to a SETTLED cycle. Orders whose cycle is still
   * active (not yet resolved) are not settleable and are excluded (issue #135).
   *
   * Uses each cycle's on-chain `isSettled` flag rather than a maxCycleId hack,
   * so a user with only prior-cycle orders (never bet the current cycle) still
   * gets the correct count.
   */
  private getSettleableUnsettledCount$(marketAddress: Address): Observable<number> {
    const addr = this.walletAddress;
    if (!addr) return of(0);

    const client = this.wallet.getPublicClient();

    return this.readContract<readonly bigint[]>({
      address: marketAddress,
      abi: PREDICT_MARKET_ABI,
      functionName: 'unsettledOrderIds',
      args: [addr],
    }).pipe(
      switchMap((orderIds) => {
        if (orderIds.length === 0) return of(0);
        // 1. Read each order to map orderId → cycleId. A single bad RPC read
        //    must NOT collapse the whole count to 0, so each call is wrapped
        //    in a per-call catch that skips only that order.
        return from(
          Promise.all(
            orderIds.map((oid) =>
              (client.readContract({
                address: marketAddress,
                abi: PREDICT_MARKET_ABI,
                functionName: 'orders',
                args: [oid],
              }) as Promise<readonly unknown[]>).catch(() => null),
            ),
          ),
        ).pipe(
          switchMap((orders) => {
            const cycleIds = orders
              .filter((o): o is readonly unknown[] => o !== null)
              .map((o) => Number(o[1]));
            if (cycleIds.length === 0) return of(0);
            const uniqueCycleIds = [...new Set(cycleIds)];
            // 2. For each unique cycleId, read `cycles[cid].isSettled`.
            //    Same per-call guard: a failed cycle read defaults to
            //    `isSettled: false`, so the worst case is undercounting a
            //    single cycle rather than zeroing the whole result.
            return from(
              Promise.all(
                uniqueCycleIds.map((cid) =>
                  (client.readContract({
                    address: marketAddress,
                    abi: PREDICT_MARKET_ABI,
                    functionName: 'cycles',
                    args: [BigInt(cid)],
                  }) as Promise<readonly unknown[]>)
                    .then((cycle) => ({ cid, isSettled: cycle[5] as boolean }))
                    .catch(() => ({ cid, isSettled: false })),
                ),
              ),
            ).pipe(
              map((cycleStates) => {
                const settled = new Set(
                  cycleStates.filter((c) => c.isSettled).map((c) => c.cid),
                );
                // 3. Only count orders whose cycle is settled (active-cycle orders excluded)
                return cycleIds.filter((cid) => settled.has(cid)).length;
              }),
            );
          }),
        );
      }),
      catchError(() => of(0)),
      take(1),
    );
  }

  // ==========================================================
  // Observable-returning methods (for dialog subscriptions)
  // ==========================================================

  /**
   * Returns an Observable<number> of unsettled order count for a market.
   * Used by settle-dialog to show per-market counts.
   *
   * Excludes orders from the current (latest) active cycle (issue #135).
   */
  getUnsettledCount$(marketAddress: Address): Observable<number> {
    return this.getSettleableUnsettledCount$(marketAddress);
  }

  /**
   * Returns Observable of the user's settleable unsettled orders for a market
   * with per-order amount + direction so the settle dialog can list each
   * order rather than just a count (#7). Same settleability filter as
   * `getSettleableUnsettledCount$`: only orders whose cycle is settled.
   */
  getUnsettledOrders$(marketAddress: Address): Observable<{ amount: bigint; direction: 'YES' | 'NO' }[]> {
    const addr = this.walletAddress;
    if (!addr) return of([]);

    const client = this.wallet.getPublicClient();

    return this.readContract<readonly bigint[]>({
      address: marketAddress,
      abi: PREDICT_MARKET_ABI,
      functionName: 'unsettledOrderIds',
      args: [addr],
    }).pipe(
      switchMap((orderIds) => {
        if (orderIds.length === 0) return of([]);
        return from(
          Promise.all(
            orderIds.map((oid) =>
              (client.readContract({
                address: marketAddress,
                abi: PREDICT_MARKET_ABI,
                functionName: 'orders',
                args: [oid],
              }) as Promise<readonly unknown[]>).catch(() => null),
            ),
          ),
        ).pipe(
          switchMap((orders) => {
            // Order tuple shape from PREDICT_MARKET_ABI:
            // [user, cycleId, amount, direction, share, timestamp, isSettled, balanceFrom]
            const decoded = orders
              .filter((o): o is readonly unknown[] => o !== null)
              .map((o) => ({
                cycleId: Number(o[1]),
                amount: o[2] as bigint,
                direction: Number(o[3]) === 0 ? ('YES' as const) : ('NO' as const),
              }));
            if (decoded.length === 0) return of([]);
            const uniqueCycleIds = [...new Set(decoded.map((d) => d.cycleId))];
            return from(
              Promise.all(
                uniqueCycleIds.map((cid) =>
                  (client.readContract({
                    address: marketAddress,
                    abi: PREDICT_MARKET_ABI,
                    functionName: 'cycles',
                    args: [BigInt(cid)],
                  }) as Promise<readonly unknown[]>)
                    .then((cycle) => ({ cid, isSettled: cycle[5] as boolean }))
                    .catch(() => ({ cid, isSettled: false })),
                ),
              ),
            ).pipe(
              map((cycleStates) => {
                const settled = new Set(
                  cycleStates.filter((c) => c.isSettled).map((c) => c.cid),
                );
                return decoded
                  .filter((d) => settled.has(d.cycleId))
                  .map(({ amount, direction }) => ({ amount, direction }));
              }),
            );
          }),
        );
      }),
      catchError(() => of([])),
      take(1),
    );
  }

  /**
   * Returns an Observable that completes when settle is done.
   * Used by settle-dialog which needs to track per-market settle status.
   */
  settleOrders$(marketAddress: Address): Observable<void> {
    const addr = this.walletAddress;
    if (!addr) return throwError(() => new Error('Wallet not connected'));

    // V2: Read unsettledOrderIds, simulate first to avoid wasting gas, then settle
    return this.readContract<readonly bigint[]>({
      address: marketAddress,
      abi: PREDICT_MARKET_ABI,
      functionName: 'unsettledOrderIds',
      args: [addr],
    }).pipe(
      switchMap((orderIds) => {
        if (orderIds.length === 0) return of(undefined as void);
        // Simulate first — if cycle isn't settled yet, this throws without wasting gas.
        return from(
          this.wallet.getPublicClient().simulateContract({
            address: marketAddress,
            abi: PREDICT_MARKET_ABI,
            functionName: 'settleOrders',
            args: [addr],
            account: addr,
          }),
        ).pipe(
          switchMap(() =>
            this.writeContract({
              address: marketAddress,
              abi: PREDICT_MARKET_ABI,
              functionName: 'settleOrders',
              args: [addr],
              account: addr,
            }),
          ),
          switchMap((txHash) =>
            from(this.wallet.getPublicClient().waitForTransactionReceipt({ hash: txHash })),
          ),
          map(() => undefined as void),
        );
      }),
      tap(() => this.wallet.bumpBalanceVersion()),
      catchError((err: Error) => throwError(() => mapSettleError(err))),
      take(1),
    );
  }

  // ==========================================================
  // Utility
  // ==========================================================

  clear(): void {
    patchState(this.state, INITIAL_STATE);
  }
}
